import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@b2b/ui-components";
import { rfqService } from "@b2b/services";
import { RfqDispute } from "@b2b/services/api/modules/rfq/rfq.types";
import { CheckCircle, XCircle, Clock } from "lucide-react";

interface AdminDisputeManagementProps {
  rfqId?: string;
}

const DISPUTE_REASONS: Record<string, string> = {
  PRICE_MISMATCH: "Price Mismatch",
  QUALITY_ISSUE: "Quality Issue",
  DELIVERY_DELAY: "Delivery Delay",
  TERMS_DISAGREEMENT: "Terms Disagreement",
  OTHER: "Other",
};

export const AdminDisputeManagement = ({
  rfqId,
}: AdminDisputeManagementProps) => {
  const [disputes, setDisputes] = useState<RfqDispute[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState<RfqDispute | null>(
    null
  );
  const [resolution, setResolution] = useState<string>("");
  const [resolveStatus, setResolveStatus] = useState<"RESOLVED" | "DISMISSED">(
    "RESOLVED"
  );
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (rfqId) {
      fetchDisputes();
    }
  }, [rfqId, statusFilter]);

  const fetchDisputes = async () => {
    if (!rfqId) return;
    try {
      setLoading(true);
      const response = await rfqService.getDisputes(rfqId);
      let filtered = Array.isArray(response) ? response : [];
      if (statusFilter !== "all") {
        filtered = filtered.filter((d) => d.status === statusFilter);
      }
      setDisputes(filtered);
    } catch (error) {
      console.error("Error fetching disputes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!selectedDispute || !rfqId) return;

    try {
      setResolving(true);
      await rfqService.resolveDispute(rfqId, selectedDispute.id, {
        resolution,
        status: resolveStatus,
      });
      setResolveDialogOpen(false);
      setSelectedDispute(null);
      setResolution("");
      fetchDisputes();
    } catch (error: any) {
      console.error("Error resolving dispute:", error);
      alert(error?.response?.data?.message || "Failed to resolve dispute");
    } finally {
      setResolving(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "RESOLVED":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "DISMISSED":
        return <XCircle className="w-4 h-4 text-gray-600" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "RESOLVED":
        return "bg-green-100 text-green-800";
      case "DISMISSED":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Dispute Management</CardTitle>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="RESOLVED">Resolved</SelectItem>
            <SelectItem value="DISMISSED">Dismissed</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading disputes...</p>
          </div>
        ) : disputes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No disputes found
          </div>
        ) : (
          <div className="space-y-4">
            {disputes.map((dispute) => (
              <div key={dispute.id} className="border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusIcon(dispute.status)}
                      <span className="font-medium">
                        {DISPUTE_REASONS[dispute.reason] || dispute.reason}
                      </span>
                      <Badge className={getStatusColor(dispute.status)}>
                        {dispute.status}
                      </Badge>
                    </div>
                    {dispute.description && (
                      <p className="text-sm text-gray-600 mb-2">
                        {dispute.description}
                      </p>
                    )}
                    <div className="text-xs text-gray-500">
                      Raised by: {dispute.raisedByRole} •{" "}
                      {new Date(dispute.createdAt).toLocaleDateString()}
                    </div>
                    {dispute.resolvedAt && (
                      <div className="text-xs text-gray-500 mt-1">
                        Resolved:{" "}
                        {new Date(dispute.resolvedAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  {dispute.status === "PENDING" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedDispute(dispute);
                        setResolveDialogOpen(true);
                      }}
                    >
                      Resolve
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Resolve Dispute Dialog */}
      <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Resolve Dispute</DialogTitle>
            <DialogDescription>
              Provide a resolution for this dispute
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                Resolution Status
              </label>
              <Select
                value={resolveStatus}
                onValueChange={(val: "RESOLVED" | "DISMISSED") =>
                  setResolveStatus(val)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="RESOLVED">Resolved</SelectItem>
                  <SelectItem value="DISMISSED">Dismissed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Resolution Details *
              </label>
              <Textarea
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                placeholder="Enter resolution details..."
                rows={4}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResolveDialogOpen(false);
                setSelectedDispute(null);
                setResolution("");
              }}
              disabled={resolving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResolve}
              disabled={!resolution || resolving}
              variant="primary"
            >
              {resolving ? "Resolving..." : "Resolve Dispute"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdminDisputeManagement;
